<script lang="ts">
  import { push } from 'svelte-spa-router';
  import Table from '../../Table.svelte';
  import type { RecentDonation } from '../../../lib/api/charityServices';
  import { getStageMeta, type LifecycleStage } from '../../../lib/donationLifecycle';

  export let donations: RecentDonation[] = [];
  export let loading = false;
  export let error: string | null = null;

  const columns = [
    {
      key: 'personName',
      label: 'Person Name',
      width: '25%',
      mobilePrimary: true
    },
    {
      key: 'employerName',
      label: 'Company',
      width: '25%',
      render: (value: string) => `<span class="company-name">${value}</span>`
    },
    {
      key: 'total',
      label: 'Total',
      width: '20%',
      render: (value: number) => `<span class="total-value">${formatCurrency(value)}</span>`
    },
    {
      key: 'status',
      label: 'Status',
      width: '30%',
      align: 'center' as const,
      render: (_value: string, row: { lifecycle_stage?: string }) => {
        const stage = (row.lifecycle_stage || 'employer_paid') as LifecycleStage;
        const meta = getStageMeta(stage);
        return `<span class="lc-badge ${meta.badgeClass}">${meta.label}</span>`;
      }
    }
  ];

  function handleRowClick(row: { donationId?: string }) {
    if (row.donationId) {
      push(`/donation-request/${row.donationId}`);
    }
  }

  function formatCurrency(cents: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  $: tableData = Array.isArray(donations) ? donations.map(donation => ({
    uuid: donation.uuid,
    donationId: donation.donationId,
    personName: donation.employeeName,
    employerName: donation.employerName || 'N/A',
    hours: donation.hours,
    total: donation.valueCents,
    status: donation.status,
    lifecycle_stage: donation.lifecycle_stage
  })) : [];
</script>

{#if loading}
  <div class="loading-container">
    <div class="loading-spinner"></div>
    <p>Loading donations...</p>
  </div>
{:else if error}
  <div class="error-container">
    <p class="error-message">{error}</p>
  </div>
{:else}
  <Table
    actionText="View All"
    actionHref="#/donation-requests"
    {columns}
    data={tableData}
    onRowClick={handleRowClick}
  />
{/if}

<style>
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #f0b429;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .error-message {
    color: #dc2626;
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  :global(.total-value) {
    font-weight: 600;
    color: #111827;
  }

  :global(.company-name) {
    color: #374151;
    font-weight: 500;
  }

  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: normal;
  }

  :global(.status-badge.status-approved) {
    background: #d1fae5;
    color: #065f46;
  }

  :global(.status-badge.status-pending) {
    background: #fef3c7;
    color: #92400e;
  }

  :global(.status-badge.status-rejected) {
    background: #fee2e2;
    color: #991b1b;
  }

  :global(.status-badge.status-paid) {
    background: #dbeafe;
    color: #1e40af;
  }
</style>
